/**
 * PrintDocumentModal — „Wybór wydruku dokumentu", odpowiednik okna z Symfonii.
 *
 * Wybiera się szablon („Nazwa raportu") i nagłówek wydruku (oryginał / kopia / duplikat), a przy
 * wydruku domyślnym dodatkowo to, czy drukować uwagi.
 *
 * TRZY RÓŻNICE WOBEC OKNA W SYMFONII — świadome, bo wynikają z tego, gdzie działamy:
 *  1. Nie ma wyboru „Podgląd / Wydruk" ani drukarki. WebAPI oddaje PDF-a, a przeglądarka nie ma
 *     dostępu do drukarki fiskalnej ani do listy drukarek stanowiska. Pokazujemy podgląd, z
 *     którego użytkownik drukuje swoją przeglądarką.
 *  2. Nie ma trybu „Graficzny / Tekstowy" — to ustawienie silnika wydruku na stanowisku.
 *  3. Lista szablonów NIE JEST zawężona do rodzaju dokumentu: Symfonia nie mówi, dla czego dany
 *     szablon jest przeznaczony, i nie broni wydrukowania faktury szablonem magazynowym. Stąd
 *     pozycja „wydruk domyślny" na pierwszym miejscu — jest bezpieczna i nią zaczynamy.
 *
 * Wybór zapamiętujemy w przeglądarce per kartoteka: kto drukuje kopie, ten drukuje je seriami.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HM_PRINT_COPY_KINDS,
  type HmListResponse,
  type HmPrintCopyKind,
  type HmPrintSettings,
  type HmPrintTemplate,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export type PrintKind = 'sales' | 'warehouse' | 'payments';

const DEFAULT_TEMPLATE = '__default__';

export function defaultPrintSettings(): HmPrintSettings {
  return {
    templateId: null,
    copyKind: 'Original',
    printNote: false,
    footer: '',
    issuedBy: '',
    receivedBy: '',
  };
}

/** Ustawienia wydruku → parametry adresu trasy PDF. Pusty wybór = wydruk domyślny. */
export function printSettingsToParams(settings: HmPrintSettings): string {
  const sp = new URLSearchParams();
  if (settings.templateId != null) {
    sp.set('templateId', String(settings.templateId));
    sp.set('copyKind', settings.copyKind);
    if (settings.footer.trim()) sp.set('footer', settings.footer.trim());
    if (settings.issuedBy.trim()) sp.set('issuedBy', settings.issuedBy.trim());
    if (settings.receivedBy.trim()) sp.set('receivedBy', settings.receivedBy.trim());
  } else if (settings.printNote) {
    // Uwagi obsługuje wyłącznie wydruk domyślny — szablon bierze treści z własnych pól.
    sp.set('printNote', 'true');
  }
  return sp.toString();
}

function storageKey(kind: PrintKind): string {
  return `hm.print.${kind}`;
}

function loadRemembered(kind: PrintKind): Partial<HmPrintSettings> {
  try {
    const raw = window.localStorage.getItem(storageKey(kind));
    return raw ? (JSON.parse(raw) as Partial<HmPrintSettings>) : {};
  } catch {
    // Prywatne okno albo zablokowana pamięć — zapamiętywanie to wygoda, nie warunek działania.
    return {};
  }
}

export function PrintDocumentModal({
  open,
  kind,
  companyId,
  documentNumber,
  onClose,
  onPrint,
}: {
  open: boolean;
  kind: PrintKind;
  companyId: string | null;
  documentNumber: string;
  onClose: () => void;
  onPrint: (settings: HmPrintSettings) => void;
}) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<HmPrintSettings>(defaultPrintSettings());

  useEffect(() => {
    if (open) setSettings({ ...defaultPrintSettings(), ...loadRemembered(kind) });
  }, [open, kind]);

  const templates = useQuery({
    queryKey: ['handel-magazyn', 'print-templates', kind, companyId],
    enabled: open && !!companyId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<HmListResponse<HmPrintTemplate>>(
          `/handel-magazyn/print-templates?kind=${kind}&companyId=${companyId}`,
        )
      ).data,
  });

  function set(patch: Partial<HmPrintSettings>) {
    setSettings((prev) => ({ ...prev, ...patch }));
  }

  function submit() {
    try {
      window.localStorage.setItem(storageKey(kind), JSON.stringify(settings));
    } catch {
      // jw. — brak pamięci nie może blokować wydruku
    }
    onPrint(settings);
  }

  const items = templates.data?.items ?? [];
  const usesTemplate = settings.templateId != null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.handelMagazyn.print.title')}
      description={t('tenant.handelMagazyn.print.subtitle', { document: documentNumber })}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={submit} data-testid="hm-print-submit">
            {t('tenant.handelMagazyn.print.show')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <label className="block text-sm">
          <span className="mb-1 block text-text-secondary">
            {t('tenant.handelMagazyn.print.template')}
          </span>
          <Dropdown
            value={settings.templateId != null ? String(settings.templateId) : DEFAULT_TEMPLATE}
            onChange={(value) =>
              set({ templateId: value === DEFAULT_TEMPLATE ? null : Number(value) })
            }
            options={[
              { value: DEFAULT_TEMPLATE, label: t('tenant.handelMagazyn.print.defaultTemplate') },
              ...items.map((tpl) => ({ value: String(tpl.id), label: tpl.name })),
            ]}
            data-testid="hm-print-template"
          />
          {templates.isError && (
            <span className="mt-1 block text-xs text-amber-700">
              {t('tenant.handelMagazyn.print.templatesFailed')}
            </span>
          )}
          {!templates.isError && !templates.isLoading && items.length === 0 && (
            <span className="mt-1 block text-xs text-text-tertiary">
              {t('tenant.handelMagazyn.print.noTemplates')}
            </span>
          )}
        </label>

        {usesTemplate ? (
          <>
            <label className="block text-sm">
              <span className="mb-1 block text-text-secondary">
                {t('tenant.handelMagazyn.print.copyKind')}
              </span>
              <Dropdown
                value={settings.copyKind}
                onChange={(value) => set({ copyKind: value as HmPrintCopyKind })}
                options={HM_PRINT_COPY_KINDS.map((copy) => ({
                  value: copy,
                  label: t(`tenant.handelMagazyn.print.copy.${copy}`),
                }))}
                data-testid="hm-print-copy"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t('tenant.handelMagazyn.print.issuedBy')}
                value={settings.issuedBy}
                onChange={(e) => set({ issuedBy: e.target.value })}
                data-testid="hm-print-issued-by"
              />
              <Input
                label={t('tenant.handelMagazyn.print.receivedBy')}
                value={settings.receivedBy}
                onChange={(e) => set({ receivedBy: e.target.value })}
                data-testid="hm-print-received-by"
              />
            </div>
            <Input
              label={t('tenant.handelMagazyn.print.footer')}
              value={settings.footer}
              onChange={(e) => set({ footer: e.target.value })}
              data-testid="hm-print-footer"
            />
            <p className="text-xs text-text-tertiary">
              {t('tenant.handelMagazyn.print.templateHint')}
            </p>
          </>
        ) : (
          <Checkbox
            checked={settings.printNote}
            onChange={(e) => set({ printNote: e.target.checked })}
            label={t('tenant.handelMagazyn.print.printNote')}
            data-testid="hm-print-note"
          />
        )}
      </div>
    </Modal>
  );
}
